import { ViewType, ViewObject } from '../base';

import { dataService } from '../../data';
import { Category } from '../objects/category';
import { ProductOpt } from '../objects/productopt';
import { CategoryDep } from '../objects/categorydep';

import { ProductView } from './product';
import { ProductOptView } from './productopt';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _CategoryView extends ViewObject<Category> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get category(): Category{
        return this.object;
    }
             
    constructor(object: Category, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _validoptions: Array <ProductOptView> | null = null;
    private _enabledoptions: Array <ProductOptView> | null = null;
    private _validdepends: Array <CategoryDep> | null = null;
    private _checkoptions: Array <ProductOptView> | null = null;
    private _issuitable: boolean | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('CATEGORY')){
            this.DoRefreshView();
        }

        if (targets.includes('OPTIONCHECKED')){
            this._issuitable = null;    
            this._validdepends = null;  
            this._enabledoptions = null;
            this._checkoptions = null; 
            this.DoRefreshView();
        }        

        if (targets.includes('PRODUCTOPT')){
            this._validoptions = null;  
            this._validdepends = null;  
            this._enabledoptions = null;
            this.DoRefreshView();
        }        
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

         // update registrations when children change
        this._subscriptions.push(
            this.category.OnChildChanged.subscribe(() => {
                const self = weakref.deref();
                if (self) self.doRegister();
            })
        );
        
        // register to updates on contained items 
        this._subscriptions.push(
            this.category.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        )    

        if (this.product){
            for(const _option of this.product.options){
                this._subscriptions.push(
                    _option.OnRefresh.subscribe(
                    (targets: string[]) => {
                        const self = weakref.deref();
                        if (self){  // do not keep strong references in subscription
                            self.RefreshView(targets);
                        }               
                    })
                )        
            }    
        }
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/    

    get product(): ProductView {
        return this.alives.get(this.category.product) as ProductView;
    }

    get options(): Array <ProductOptView> {
        if (this._validoptions === null){
            const _options: Array <ProductOptView> = []; 

            for(const _option of this.category.options){
                if (_option.IsValid){
                    _options.push(this.alives.get(_option) as ProductOptView);

                }
            }

            _options.sort((a, b) => {
                return (a.sort || 0) - (b.sort || 0);
            })

            this._validoptions = _options;
        }

        return this._validoptions;
    }

    get enabled(): Array <ProductOptView> {
        if (this._enabledoptions === null){
            const _options: Array <ProductOptView> = []; 

            for (const _option of this.options){
                if (_option.Enabled){
                    _options.push(_option);
                }
            }

            _options.sort((a, b) => {
                return (a.sort || 0) - (b.sort || 0);
            })
                        
            this._enabledoptions = _options;
        }

        return this._enabledoptions;
    }

    get depends(): Array <CategoryDep> {
        if (this._validdepends === null){
            const _depends: Array <CategoryDep> = [];
            for (const depend of this.category.depends){
                if (depend.IsValid){
                    _depends.push(depend);
                }
            }

            this._validdepends = _depends;
        }

        return this._validdepends;
    }

    /**********************************/
    /* CATEGORY DEPENDS               */
    /**********************************/

    get Enabled(){
        return (this.enabled.length > 0);
    }

    private GetDepend(option: ProductOpt): CategoryDep | null {
        for (const _categorydep of this.category.depends){
            if (_categorydep.IsValid && (_categorydep.option == option)){
                return _categorydep;
            }
        }

        return null;    // not found
    }

    /**
     * Evaluates whether the provided option is a dependency of this category
     *    
     * @returns {boolean} True if the given option is a dependency
     */    
    HasDepend(option: ProductOpt): boolean {
        return (this.GetDepend(option) != null);
    }

    /**
     * Includes the product option as a dependency for this category
     */    
    AddDepend(option: ProductOpt): void {
        if (!option || (this.GetDepend(option) != null)){
            return;
        }

        const _depend = new CategoryDep(null, this.data);
        if (_depend){
            _depend.category = this.category;
            _depend.option = option;

            this.category.AddDepend(_depend);
        }
    }

    /**
     * Removes the product option as a dependency for this category
     */     
    DelDepend(option: ProductOpt): void {
        if (!option){
            return;
        }

        const _depend = this.GetDepend(option);
        if (_depend){
            _depend.status = 'DE';
        }
    }

    /**
     * Evaluates whether the current category should be displayed based on its dependencies.
     * 
     * This getter evaluates all dependencies defined in the category to determine
     * if this category should be shown to the user. It checks if all required options
     * from other categories have been selected.
     * 
     * @returns {boolean} True if all dependency conditions are met and the category should be displayed
     * 
     */
    get Suitable(): boolean{
        if (this._issuitable === null){
            this._issuitable = this._Suitable;
        }

        return this._issuitable;
    }

    /**
     * True when the category is not a catalog template and has at least one available option.
     */
    get IsAvailable(): boolean {
        if (this.category.IsCatalog){
            return false;
        }

        return this.options.some(option => option.IsAvailable);
    }

    /**
     * Performs the calculations to determine whether the category is suitable
     */
    private get _Suitable(): boolean {
        if (this.category.options.length == 0){
            return false;   // no options for this category
        }
        
        if (this.category.depends.length == 0){
            return true;    // no dependendencies for this category
        }

        return this.category.depends.some(_depend => {
            const _optView: ProductOptView = this.data.alives.get(_depend.option) as ProductOptView;
            if (!_optView){
                return false;
            }

            return _optView.Checked;    // al least one dependency is checked
        })
    }   

    /**********************************/
    /* CATEGORY SELECTION             */
    /**********************************/    

    MarkEnabled(){
        for (const _option of this.options){
            _option.Enabled = true;
        }
    }

    ResetChecked(){
        for (const _option of this.options){
            _option.Checked = false;
        }
    }

    /**
     * Obtains the selected item for SINGLE categories
     */        
    get SingleOption(): ProductOptView | null {
        if (this.category.type != 'SINGLE'){
            return null;    // not applicable
        }

        for (const _option of this.options){
            if (_option.Checked){
                return _option;
            }
        }

        return null;    // no option checked 
    }

    /**
     * Updates the selected item for SINGLE categories
     */        
    set SingleOption(value: ProductOptView | null){
        if (this.category.type != 'SINGLE'){
            return;    // not applicable
        }

        let _singleoption = this.SingleOption;
        if (_singleoption){
            _singleoption.Checked = false;
        }

        _singleoption = value;

        if (_singleoption){
            _singleoption.Checked = true;
        }
    } 

    /**
     * Obtains the list of the selected options within this category
     */     
    get Selected(): Array <ProductOptView> {
        if (this._checkoptions === null){
            const _selected: Array <ProductOptView> = [];

            for (const _option of this.options){
                if (_option.Checked){
                    _selected.push(_option);
                }
            }
    
            this._checkoptions = _selected;    
        }

        return this._checkoptions;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type CategoryView = ViewType <_CategoryView, Category>;

export function CategoryViewProxy(object: Category, data: dataService): CategoryView {
    return new _CategoryView(object, data).Proxy as CategoryView;
}
